/**
 * Grail homepage behaviour, loaded only by the sections that use it.
 *
 *   <grail-rail>       Previous / next buttons for a horizontal scroll-snap
 *                      rail. The rail scrolls natively without JS; the
 *                      buttons just page it and disable at either end.
 *   <grail-countdown>  Days / hours / minutes to a release date. The server
 *                      renders the date itself as a static fallback; this
 *                      only fills in the figures, once a minute (no ticking
 *                      seconds, so nothing on screen moves continuously).
 *                      data-date is an ISO timestamp with the store's offset
 *                      (2026-10-01T00:00:00-0400) or a plain YYYY-MM-DD, which
 *                      counts to midnight in the store's timezone
 *                      (window.grail.utcOffset), not the shopper's.
 */
(() => {
  if (!customElements.get('grail-rail')) {
    class GrailRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rail-track]');
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        if (!this.track || !this.prev || !this.next) return;

        this.onScroll = () => this.update();
        this.prev.addEventListener('click', this.onPrev);
        this.next.addEventListener('click', this.onNext);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        // Removed here so a move (theme editor reorder) doesn't stack a second set.
        this.prev?.removeEventListener('click', this.onPrev);
        this.next?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      onPrev = () => this.page(-1);

      onNext = () => this.page(1);

      page(direction) {
        const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({
          left: direction * rtl * this.track.clientWidth * 0.85,
          behavior: reduce ? 'auto' : 'smooth',
        });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const position = Math.abs(scrollLeft);
        const overflow = scrollWidth - clientWidth > 2;
        this.toggleAttribute('data-overflow', overflow);
        this.prev.disabled = !overflow || position <= 2;
        this.next.disabled = !overflow || position >= scrollWidth - clientWidth - 2;
      }
    }
    customElements.define('grail-rail', GrailRail);
  }

  if (!customElements.get('grail-countdown')) {
    class GrailCountdown extends HTMLElement {
      connectedCallback() {
        this.target = GrailCountdown.parse(this.dataset.date || '');
        if (Number.isNaN(this.target)) return;
        this.render();
        this.timer = window.setInterval(() => this.render(), 30000);
      }

      disconnectedCallback() {
        window.clearInterval(this.timer);
      }

      /** Release time in ms. Liquid's %z gives +HHMM; Date.parse needs +HH:MM. */
      static parse(value) {
        const offset = (text) => text.replace(/^([+-]\d{2}):?(\d{2})$/, '$1:$2');
        const full = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?)([+-]\d{2}:?\d{2}|Z)$/);
        if (full) return Date.parse(full[1] + (full[2] === 'Z' ? 'Z' : offset(full[2])));
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
        const storeOffset = /^[+-]\d{2}:?\d{2}$/.test(window.grail?.utcOffset || '') ? offset(window.grail.utcOffset) : null;
        if (storeOffset) return Date.parse(`${value}T00:00:00${storeOffset}`);
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day).getTime(); // no store offset available: shopper's midnight
      }

      render() {
        const remaining = this.target - Date.now();
        if (remaining <= 0) {
          this.setAttribute('data-released', '');
          window.clearInterval(this.timer);
          return;
        }
        const minutes = Math.floor(remaining / 60000);
        const values = {
          days: Math.floor(minutes / 1440),
          hours: Math.floor((minutes % 1440) / 60),
          minutes: minutes % 60,
        };
        Object.entries(values).forEach(([unit, value]) => {
          const el = this.querySelector(`[data-unit="${unit}"]`);
          if (el) el.textContent = String(value).padStart(2, '0');
        });
        this.setAttribute('data-ready', '');
      }
    }
    customElements.define('grail-countdown', GrailCountdown);
  }
})();
